import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import { TrackEntry } from '@esotericsoftware/spine-pixi-v8';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { IAuthenticateGameConfig } from '@GFTypes/GameStoreTypes/AuthenticateGameConfig';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import { EActionName } from '@Managers/Events/EActionName';
import EventsCenter from '@Managers/Events/EventsCenter';
import { ALL_EVENTS_LIST, EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateConditions } from '@Managers/StateManager/StateConditions';
import randomValue from '@Tools/RandomValue';
import { Container } from 'pixi.js';
import { GambleCard } from './Elements/GambleCard';
import { GambleResult } from './Elements/GambleResult';
import { GambleText } from './Elements/GambleText';

export enum GAMBLER_TEXT_SLOTS {
    WIN_VALUE = 'txt_win_value',
    WIN_TEXT = 'T_GAMBLE_WIN',
    SAVE_VALUE = 'txt_save_value',
    SAVE_TEXT = 'T_GAMBLE_SAVE',
    RISK_VALUE = 'txt_risk_value',
    RISK_TEXT = 'T_GAMBLE_RISK',
    DESCRIPTION_TEXT = 'T_GAMBLE_INFO'
}

export class Gamble extends GameComponent {
    static readonly NUMBER_OF_CARDS: number = 4;
    static readonly BACKGROUND_SPINE_KEY: string = 'gamble_background';
    static readonly SKIN_NAME: string = 'default';
    public scene: GameScene;
    private gambleBackendConfiguration: IAuthenticateGameConfig['gamble'];
    public gambleSpine: SpineWrapper;
    public gambleElements: Container;
    public cards: GambleCard[] = [];
    private gambleResult: GambleResult;
    private winText: GambleText;
    private winValueText: GambleText;
    private saveText: GambleText;
    private saveValueText: GambleText;
    private riskText: GambleText;
    private riskValueText: GambleText;
    private descriptionText: GambleText;
    private canPick: boolean;
    private canChangeRisk: boolean;
    private skipFirstShuffle: boolean;
    constructor(scene: GameScene) {
        super(scene);
        this.scene = scene;
        Logger.log(LoggerLevel.Constructor, {
            source: LoggerSource.Game,
            message: 'Gamble constructor'
        });

        EventsCenter.on(EGameStates.PRE_GAMBLE_STATE, this.onGambleStart, this);
        EventsCenter.on(EGameStates.GAMBLE_STATE, this.onGambleShuffle, this);
        EventsCenter.on(EGameEvents.ACTION_RESPONSE_PROCESSED, this.onGambleResultReceived, this);
        EventsCenter.on(EGameStates.GAMBLE_RESULT_STATE, this.onGambleResult, this);
        EventsCenter.on(ALL_EVENTS_LIST.TAKE, this.onTake, this);
        EventsCenter.on(EGameStates.POST_GAMBLE_STATE, this.onGambleEnd, this);
    }

    public onInit(): void {
        this.gambleBackendConfiguration = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).gamble;
    }

    public onCreate(): void {
        this.gambleSpine = new SpineWrapper(
            this.scene,
            {
                x: this.scene.screen.width / 2,
                y: this.scene.screen.height / 2
            },
            { dataKey: Gamble.BACKGROUND_SPINE_KEY, skinName: Gamble.SKIN_NAME },
            true
        );

        this.gambleSpine.spine.zIndex = ELayeringOrder.GAMBLE;
        this.gambleElements = new Container({ parent: this.scene.stage });
        this.gambleElements.zIndex = ELayeringOrder.GAMBLE_ELEMENTS;
        //TODO: Migrate to PIXI
        // this.gambleSpine.spine.setInteractive();
        // this.gambleSpine.spine.on('pointerdown', this.onClick, this);

        this.gambleResult = new GambleResult(this);

        this.createTexts();
        this.generateCards();

        this.gambleSpine.spine.visible = false;
        this.gambleElements.visible = false;
    }

    private createTexts(): void {
        this.winText = new GambleText(this, GAMBLER_TEXT_SLOTS.WIN_TEXT);
        this.winValueText = new GambleText(this, GAMBLER_TEXT_SLOTS.WIN_VALUE);
        this.saveText = new GambleText(this, GAMBLER_TEXT_SLOTS.SAVE_TEXT);
        this.saveValueText = new GambleText(this, GAMBLER_TEXT_SLOTS.SAVE_VALUE);
        this.riskText = new GambleText(this, GAMBLER_TEXT_SLOTS.RISK_TEXT);
        this.riskValueText = new GambleText(this, GAMBLER_TEXT_SLOTS.RISK_VALUE);
        this.descriptionText = new GambleText(this, GAMBLER_TEXT_SLOTS.DESCRIPTION_TEXT);
    }

    private generateCards(): void {
        for (let i = 0; i < Gamble.NUMBER_OF_CARDS; i++) {
            this.cards.push(new GambleCard(this, i));
        }
    }

    public onGambleStart(): void {
        this.resetValues();
        this.updateTexts();
        this.toggleFullButton(false);
        this.toggleHalfButton(false);
        this.gameStore.set(EGameStoreKeys.GambleState, { gambleRequested: false }, true);
        this.gambleSpine.spine.visible = true;
        this.gambleResult.resultSpine.spine.visible = true;
        this.waitForPreAnimation();
        this.skipFirstShuffle = true;
    }

    private async waitForPreAnimation(): Promise<void> {
        await this.gambleSpine.waitForAnimationEvent(ALL_EVENTS_LIST.PRE_GAMBLE_STATE, 'complete').promise;
        StateConditions.preGambleCondition.resolve();
        this.gambleElements.visible = true;
        this.canPick = true;
        this.toggleFullButton(false);
        if (this.gameStore.get(EGameStoreKeys.GambleState).gambleValue >= this.gambleBackendConfiguration.halfMinValue.raw) this.toggleHalfButton(true);
        else this.toggleHalfButton(false);
    }

    private async onGambleShuffle(): Promise<void> {
        if (this.skipFirstShuffle) {
            this.skipFirstShuffle = false;
            return;
        }

        this.gambleElements.visible = false;
        this.resetValues();
        this.updateTexts();
        this.toggleFullButton(false);

        if (this.gameStore.get(EGameStoreKeys.GambleState).gambleValue >= this.gambleBackendConfiguration.halfMinValue.raw) this.toggleHalfButton(true);
        else this.toggleHalfButton(false);

        const shuffleTrack = this.gambleSpine.play('GAMBLE_SHUFFLE');
        //await this.gambleSpine.waitForTrackEvent(shuffleTrack, 'complete', 0).promise;
        this.canPick = true;
        this.gambleElements.visible = true;
    }

    private resetValues(): void {
        let win: number;
        if (this.gameStore.get(EGameStoreKeys.GambleState).tries === 0) win = this.gameStore.get(EGameStoreKeys.SpinOutcome).win;
        else win = this.gameStore.get(EGameStoreKeys.GambleResponse).gamble!?.amount;
        this.gameStore.set(EGameStoreKeys.GambleState, { halfingCount: 0, gambleValue: win, saveValue: 0, winValue: win * 2 }, true);
        this.canPick = false;
        this.canChangeRisk = true;
    }

    //TODO: Migrate to PIXI
    private updateTexts(): void {
        const { gambleValue, saveValue, winValue } = this.gameStore.get(EGameStoreKeys.GambleState);
        // this.riskValueText.text.setText(CurrencyUtils.formatRawCurrencyValue(gambleValue, { withSymbol: false, decimalSeparator: ',' }));
        // this.saveValueText.text.setText(CurrencyUtils.formatRawCurrencyValue(saveValue, { withSymbol: false, decimalSeparator: ',' }));
        // this.winValueText.text.setText(CurrencyUtils.formatRawCurrencyValue(winValue, { withSymbol: false, decimalSeparator: ',' }));
    }

    private onClick(pointer: any): void {
        // if (this.canChangeRisk) {
        //     if (isBoundingBoxClicked(this.gambleSpine, 'gamble_button_half_area', pointer)) this.onHalf();
        //     if (isBoundingBoxClicked(this.gambleSpine, 'gamble_button_full_area', pointer)) this.onFull();
        // }
        // if (!this.canPick) return;
        // for (let i = 0; i < Gamble.NUMBER_OF_CARDS; i++) {
        //     if (this.cards[i].isCardHit(pointer)) {
        //         this.onCardSelection(i);
        //         this.canPick = false;
        //         return;
        //     }
        // }
    }

    private onCardSelection(id: number): void {
        this.canChangeRisk = false;
        this.toggleHalfButton(false);
        this.toggleFullButton(false);
        const { tries, gambleValue } = this.gameStore.get(EGameStoreKeys.GambleState);

        this.gameStore.set(EGameStoreKeys.GambleState, { pickedCard: id, tries: tries + 1 }, true);

        EventsCenter.emit(EGameEvents.SEND_ACTION_REQUEST, { actionName: EActionName.GAMBLE, params: { gambleAmount: gambleValue, option: 0 } });
    }

    private toggleHalfButton(show: boolean): void {
        const slot = this.gambleSpine.spine.skeleton.findSlot('gamble_button_half')!;
        show ? (slot.getAppliedPose().color.a = 1) : (slot.getAppliedPose().color.a = 0);
    }

    private toggleFullButton(show: boolean): void {
        const slot = this.gambleSpine.spine.skeleton.findSlot('gamble_button_full')!;
        show ? (slot.getAppliedPose().color.a = 1) : (slot.getAppliedPose().color.a = 0);
    }

    private onHalf(): void {
        const { halfingCount, gambleValue, saveValue } = this.gameStore.get(EGameStoreKeys.GambleState);

        if (halfingCount >= this.gambleBackendConfiguration.halfTimes || gambleValue < this.gambleBackendConfiguration.halfMinValue.raw) return;

        this.gameStore.set(
            EGameStoreKeys.GambleState,
            {
                halfingCount: halfingCount + 1,
                gambleValue: Math.floor(gambleValue / 2),
                saveValue: saveValue + Math.ceil(gambleValue / 2),
                winValue: Math.floor(gambleValue / 2) * 2
            },
            true
        );

        this.updateTexts();
        this.toggleFullButton(true);

        const halfingCountUpdated = this.gameStore.get(EGameStoreKeys.GambleState).halfingCount;
        const gambleValueUpdated = this.gameStore.get(EGameStoreKeys.GambleState).gambleValue;
        if (halfingCountUpdated >= this.gambleBackendConfiguration.halfTimes || gambleValueUpdated < this.gambleBackendConfiguration.halfMinValue.raw)
            this.toggleHalfButton(false);
    }

    private onFull(): void {
        const { halfingCount, tries } = this.gameStore.get(EGameStoreKeys.GambleState);
        let win: number;
        if (tries <= 0) win = this.gameStore.get(EGameStoreKeys.SpinOutcome).win;
        else win = this.gameStore.get(EGameStoreKeys.GambleResponse)?.gamble?.amount!;

        if (halfingCount === 0) return;

        this.gameStore.set(EGameStoreKeys.GambleState, { halfingCount: 0, gambleValue: win, saveValue: 0, winValue: win * 2 }, true);

        this.updateTexts();
        this.toggleFullButton(false);
        this.toggleHalfButton(true);
    }

    private async onGambleResultReceived(action: { actionName: EActionName }): Promise<void> {
        if (action.actionName !== EActionName.GAMBLE) return;
        StateConditions.gambleCondition.resolve();
    }

    private async onGambleResult(): Promise<void> {
        if (this.canPick || this.gameStore.get(EGameStoreKeys.IsTakeInitiated)) return;
        const { amount } = this.gameStore.get(EGameStoreKeys.GambleResponse).gamble!;
        const { pickedCard, saveValue, tries } = this.gameStore.get(EGameStoreKeys.GambleState);
        const isWin = amount > saveValue;
        const allCards = Array.from({ length: Gamble.NUMBER_OF_CARDS }, (_, i) => i);
        const losingCards: number[] = [];
        const winningCards: number[] = [];
        let resultTrack: TrackEntry;

        if (isWin) {
            winningCards.push(allCards.splice(allCards.indexOf(pickedCard), 1)[0]);
            winningCards.push(allCards.splice(randomValue(0, Gamble.NUMBER_OF_CARDS - 2), 1)[0]);
            losingCards.push(...allCards);

            resultTrack = this.gambleResult.resultSpine.play('GAMBLE_WIN')!;
        } else {
            losingCards.push(allCards.splice(allCards.indexOf(pickedCard), 1)[0]);
            losingCards.push(allCards.splice(randomValue(0, Gamble.NUMBER_OF_CARDS - 2), 1)[0]);
            winningCards.push(...allCards);

            resultTrack = this.gambleResult.resultSpine.play('GAMBLE_LOSE')!;
        }

        this.cards.forEach((card, index) => {
            if (winningCards.includes(index)) index === pickedCard ? card.cardSpine.play('GAMBLE_WIN') : card.cardSpine.play('GAMBLE_NOTWIN_JOKER');
            if (losingCards.includes(index)) index === pickedCard ? card.cardSpine.play('GAMBLE_LOSE') : card.cardSpine.play('GAMBLE_NOTWIN_EMPTY');
        });

        await this.gambleResult.resultSpine.waitForTrackEvent(resultTrack, 'complete').promise;

        const isFinished = amount === 0 || tries >= this.gambleBackendConfiguration.maxTries;

        if (isFinished) {
            this.gameStore.set(EGameStoreKeys.IsTakeInitiated, true);
            this.gameStore.set(EGameStoreKeys.IsTakeCompleted, true);
            StateConditions.gambleResultCondition.resolve();
            return;
        }

        StateConditions.gambleResultCondition.resolve();
    }

    private onTake(): void {
        if (this.gameStore.get(EGameStoreKeys.CurrentGameState) !== EGameStates.GAMBLE_STATE) return;
        StateConditions.gambleCondition.resolve();
        StateConditions.gambleResultCondition.resolve();
    }

    private async onGambleEnd(): Promise<void> {
        const currentState = this.gameStore.get(EGameStoreKeys.CurrentGameState);

        if (currentState !== EGameStates.POST_GAMBLE_STATE) return;

        await this.gambleSpine.waitForAnimationEvent(EGameStates.POST_GAMBLE_STATE, 'complete').promise;

        this.gambleSpine.spine.state.clearTracks();
        this.gambleSpine.spine.skeleton.setupPose();
        this.gambleSpine.spine.visible = false;
        this.gambleElements.visible = false;
        this.gambleResult.resultSpine.spine.visible = false;
        this.gameStore.set(EGameStoreKeys.GambleState, { tries: 0, halfingCount: 0, gambleValue: 0, saveValue: 0, winValue: 0, pickedCard: undefined }, true);
        StateConditions.postGambleCondition.resolve();
    }
}
